import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { GitMerge, Search } from 'lucide-react';
import { Button, CardTitle, CheckLabel, Empty, Input, Message } from '../admin/ui';
import * as Api from './PersonAdminApi';
import { errorText } from './personAdminModel';
import { valueText } from './personAdminParts';
import { referenceRows } from './referenceLabels';

// Merging a duplicate into this person or this person into a duplicate (admins only; owner
// 2026-10-25: the admin must know exactly what happens). The admin finds the other person, the card
// decides which row stays, shows the backend's preview in plain words, and merges only after that:
//   - the person with the login always stays (the backend refuses the other way round);
//   - neither has a login: this page's person stays unless the admin swaps;
//   - both have one: the admin picks the login that stays, the other login is deleted in the same
//     step, which the admin confirms with a checkbox.
// After the merge the screen shows the person that stayed (onMerged reloads this page's person).

const Section = ({ title, children }) => (
  <section style={{ marginTop: '.9rem' }}>
    <h3 style={{ fontSize: '.85rem', margin: '0 0 .35rem', color: '#334155' }}>{title}</h3>
    {children}
  </section>
);
const Ul = ({ children }) => <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.85rem', lineHeight: 1.5 }}>{children}</ul>;

const MergeCard = ({ person, personId, onMerged }) => {
  const intl = useIntl();
  const t = useCallback((id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values), [intl]);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [other, setOther] = useState(null);
  // Which row stays when nothing forces it: 'this' or 'other'.
  const [keep, setKeep] = useState('this');
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const thisHasLogin = !!person.account;
  const otherHasLogin = !!(other && other.hasAccount);
  // The login holder stays when only one has a login; otherwise the admin's choice.
  const keepThis = other == null ? true
    : thisHasLogin !== otherHasLogin ? thisHasLogin : keep === 'this';
  const survivorId = other == null ? null : (keepThis ? personId : other.id);
  const absorbedId = other == null ? null : (keepThis ? other.id : personId);

  useEffect(() => {
    if (survivorId == null) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreview(null);
    setConfirmed(false);
    setError(null);
    Api.previewMerge(survivorId, absorbedId)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((e) => { if (!cancelled) setError(errorText(e, t('personAdmin.merge.previewFailed', 'Could not compute what the merge would do.'))); });
    return () => { cancelled = true; };
  }, [survivorId, absorbedId, t]);

  const search = async (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    try {
      const page = await Api.fetchPeople({ search: query.trim() }, { size: 10 });
      setResults((page.content || []).filter((row) => row.id !== personId));
    } catch (e) {
      setError(errorText(e, t('personAdmin.merge.searchFailed', 'The search failed.')));
    }
  };

  const pick = (row) => {
    setOther(row);
    setKeep('this');
    setResults(null);
  };

  const merge = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await Api.mergePeople(survivorId, absorbedId, { removeAbsorbedLogin: !!preview.removesLogin });
      setOther(null);
      setPreview(null);
      if (result.survivorId !== personId) navigate(`/admin/people/${result.survivorId}`);
      else onMerged();
    } catch (e) {
      setError(errorText(e, t('personAdmin.merge.failed', 'The merge was refused.')));
    } finally {
      setBusy(false);
    }
  };

  const field = (key) => t(`personAdmin.field.${key}`, key);
  const moved = preview ? referenceRows(t, preview.movedRows) : [];
  const canMerge = preview && !preview.refused && (!preview.removesLogin || confirmed);

  return (
    <>
      <CardTitle><FormattedMessage id="personAdmin.merge.title" defaultMessage="Merge duplicates" /></CardTitle>
      <p style={{ margin: '0 0 .75rem', fontSize: '.82rem', color: '#64748b' }}>
        <FormattedMessage id="personAdmin.merge.intro"
          defaultMessage="When two rows are the same human, merge them into one. Find the other row first; you see exactly what happens before anything changes." />
      </p>

      {!other && (
        <>
          <form onSubmit={search} style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} style={{ flex: '1 1 16rem' }}
              aria-label={t('personAdmin.merge.search', 'Name or email of the other person')}
              placeholder={t('personAdmin.merge.search', 'Name or email of the other person')} />
            <Button type="submit" secondary><Search size={16} /><FormattedMessage id="personAdmin.merge.searchButton" defaultMessage="Search" /></Button>
          </form>
          {results !== null && (results.length === 0 ? (
            <Empty style={{ padding: '.5rem 0', textAlign: 'left' }}>
              <FormattedMessage id="personAdmin.merge.none" defaultMessage="Nobody else found." />
            </Empty>
          ) : (
            <Ul>
              {results.map((row) => (
                <li key={row.id}>
                  <button type="button" onClick={() => pick(row)}
                    style={{ border: 0, background: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, font: 'inherit' }}>
                    {row.programName || `#${row.id}`}
                  </button>
                  {row.institution ? ` · ${row.institution}` : ''}
                  {row.contactEmail ? ` · ${row.contactEmail}` : ''}
                  {row.hasAccount ? ` · ${t('personAdmin.merge.hasLogin', 'has a login')}` : ''}
                </li>
              ))}
            </Ul>
          ))}
        </>
      )}

      {other && (
        <div>
          <p style={{ margin: '0 0 .5rem', fontSize: '.85rem' }}>
            <FormattedMessage id="personAdmin.merge.pair" defaultMessage="Merging with {name}."
              values={{ name: <strong>{other.programName || `#${other.id}`}</strong> }} />
            {' '}
            <button type="button" onClick={() => { setOther(null); setPreview(null); }}
              style={{ border: 0, background: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, font: 'inherit' }}>
              <FormattedMessage id="personAdmin.merge.another" defaultMessage="Choose someone else" />
            </button>
          </p>

          {thisHasLogin === otherHasLogin && (
            <fieldset style={{ border: '1px solid #e2e8f0', padding: '.6rem .8rem', margin: '0 0 .5rem' }}>
              <legend style={{ fontSize: '.8rem', fontWeight: 700, padding: '0 .3rem' }}>
                {thisHasLogin
                  ? <FormattedMessage id="personAdmin.merge.whichLogin" defaultMessage="Both have a login. Which login stays?" />
                  : <FormattedMessage id="personAdmin.merge.whichRow" defaultMessage="Which row stays?" />}
              </legend>
              {[['this', person.programName, person.account && person.account.email],
                ['other', other.programName, otherHasLogin ? other.contactEmail : null]].map(([value, name, email]) => (
                <CheckLabel key={value}>
                  <input type="radio" name="merge-keep" checked={keep === value} onChange={() => setKeep(value)} />
                  {name || '—'}{email ? ` (${email})` : ''}
                  {value === 'this' ? ` — ${t('personAdmin.merge.thisPage', 'this page')}` : ''}
                </CheckLabel>
              ))}
            </fieldset>
          )}
          {thisHasLogin !== otherHasLogin && (
            <p style={{ margin: '0 0 .5rem', fontSize: '.82rem', color: '#64748b' }}>
              <FormattedMessage id="personAdmin.merge.loginStays"
                defaultMessage="Only one of them has a login, so that person stays and keeps the login." />
            </p>
          )}

          {error && <Message error role="alert">{error}</Message>}
          {!preview && !error && <Empty style={{ padding: '.5rem 0', textAlign: 'left' }}><FormattedMessage id="personAdmin.merge.computing" defaultMessage="Working out what would happen…" /></Empty>}

          {preview && (
            <div>
              <Message warn={!preview.refused} error={preview.refused} role="status">
                <strong>
                  <FormattedMessage id="personAdmin.merge.summary"
                    defaultMessage="{survivor} stays. {absorbed} is merged into them and disappears from every list."
                    values={{ survivor: preview.survivorName || `#${preview.survivorId}`, absorbed: preview.absorbedName || `#${preview.absorbedId}` }} />
                </strong>
                {preview.refused && (
                  <div style={{ marginTop: '.4rem' }}>
                    <FormattedMessage id="personAdmin.merge.refused" defaultMessage="This merge is not possible: {reason}" values={{ reason: preview.refusal }} />
                  </div>
                )}
              </Message>

              <Section title={t('personAdmin.merge.login', 'Login')}>
                <Ul>
                  {preview.removesLogin ? (
                    <>
                      <li>
                        <FormattedMessage id="personAdmin.merge.loginKept" defaultMessage="The login {email} stays; the person logs in with it from now on."
                          values={{ email: <strong>{preview.survivorLogin.email}</strong> }} />
                      </li>
                      <li>
                        <FormattedMessage id="personAdmin.merge.loginRemoved"
                          defaultMessage="The login {email} is deleted. That address can no longer log in. What was done with it (registrations, abstracts, decisions) moves to the login that stays."
                          values={{ email: <strong>{preview.absorbedLogin.email}</strong> }} />
                      </li>
                    </>
                  ) : preview.survivorLogin ? (
                    <li><FormattedMessage id="personAdmin.merge.loginUnchanged" defaultMessage="The login {email} stays as it is."
                      values={{ email: preview.survivorLogin.email }} /></li>
                  ) : (
                    <li><FormattedMessage id="personAdmin.merge.noLogin" defaultMessage="Neither has a login." /></li>
                  )}
                </Ul>
              </Section>

              <Section title={t('personAdmin.merge.filled', 'Filled in from the merged row (empty fields only)')}>
                {preview.fills.length === 0 ? (
                  <Empty style={{ padding: 0, textAlign: 'left' }}><FormattedMessage id="personAdmin.merge.nothing" defaultMessage="Nothing." /></Empty>
                ) : (
                  <Ul>{preview.fills.map((f) => <li key={f.field}>{field(f.field)}: {valueText(t, f.field, f.absorbedValue)}</li>)}</Ul>
                )}
              </Section>

              <Section title={t('personAdmin.merge.conflicts', 'Different in both: the value of the person who stays is kept')}>
                {preview.conflicts.length === 0 ? (
                  <Empty style={{ padding: 0, textAlign: 'left' }}><FormattedMessage id="personAdmin.merge.nothing" defaultMessage="Nothing." /></Empty>
                ) : (
                  <Ul>
                    {preview.conflicts.map((f) => (
                      <li key={f.field}>
                        {field(f.field)}: <strong>{valueText(t, f.field, f.survivorValue)}</strong>
                        {' '}<span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{valueText(t, f.field, f.absorbedValue)}</span>
                      </li>
                    ))}
                  </Ul>
                )}
              </Section>

              <Section title={t('personAdmin.merge.moved', 'Moved to the person who stays')}>
                {moved.length === 0 ? (
                  <Empty style={{ padding: 0, textAlign: 'left' }}><FormattedMessage id="personAdmin.merge.nothing" defaultMessage="Nothing." /></Empty>
                ) : (
                  <Ul>{moved.map((row) => <li key={row.key}>{row.label}: {row.count}</li>)}</Ul>
                )}
                <p style={{ margin: '.4rem 0 0', fontSize: '.78rem', color: '#64748b' }}>
                  <FormattedMessage id="personAdmin.merge.duplicatesNote"
                    defaultMessage="Where both have the same thing (a registration for one event, a membership in one body), one is kept." />
                </p>
              </Section>

              {!preview.refused && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 .5rem', fontSize: '.82rem', fontWeight: 700, color: '#991b1b' }}>
                    <FormattedMessage id="personAdmin.merge.final" defaultMessage="A merge cannot be undone." />
                  </p>
                  {preview.removesLogin && (
                    <CheckLabel style={{ marginBottom: '.6rem' }}>
                      <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                      <FormattedMessage id="personAdmin.merge.confirmLogin" defaultMessage="I understand that the login {email} is deleted."
                        values={{ email: preview.absorbedLogin.email }} />
                    </CheckLabel>
                  )}
                  <Button type="button" danger disabled={!canMerge || busy} onClick={merge}>
                    <GitMerge size={16} /><FormattedMessage id="personAdmin.merge.button" defaultMessage="Merge" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MergeCard;
